import {
    ChoiceListBase,
    ChoiceListItem,
    Radio,
} from '@eventbrite/design-ops-ds';
import React from 'react';
import {
    childClampState,
    ReadMore,
    ReadMoreContent,
    ReadMoreToggle,
} from '../../../../components/ReadMore';
import './FilterSection.scss';
import { FilterItemProps, FilterSectionProps } from './types';

const PREVIEW_CHILD_COUNT = 4;
const SCROLL_ROOT = 'animated-drawer__content';
const HEADER_OFFSET_CLASS = 'marmalade-filter-panel-mobile-header';

export const getRadioValues = (filters: object) => {
    const entries = Object.entries(filters);

    return {
        radioValues: entries.map(([key, label]) => ({
            display: label,
            value: key,
        })),
    };
};

export const FilterItem: React.FunctionComponent<FilterItemProps> = ({
    ChoiceComponent = Radio,
    value,
    display,
    children,
}) => {
    return (
        <>
            <ChoiceListItem value={value} label={display}>
                <ChoiceComponent />
            </ChoiceListItem>
            {children}
        </>
    );
};

function useShouldScrollIntoView(shouldScrollIntoView: boolean) {
    const node = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (shouldScrollIntoView) {
            setTimeout(() => {
                const offset = document
                    .getElementsByClassName(HEADER_OFFSET_CLASS)[0]
                    .getBoundingClientRect().height;
                const scrollRoot =
                    document.getElementsByClassName(SCROLL_ROOT)[0];

                const rect = node?.current?.getBoundingClientRect();
                const top = rect?.top || 135;
                const scrollDelta = top - offset;

                requestAnimationFrame(() => {
                    scrollRoot.scrollBy({
                        top: scrollDelta,
                        behavior: 'smooth',
                    });
                });
            }, 500);
        }
    }, [shouldScrollIntoView]);

    return node;
}

export const FilterSection = ({
    filters,
    onOptionChange,
    title,
    activeFilterOption,
    isMobile,
    defaultActiveFilterOption = '',
    children,
    shouldScrollIntoView,
}: FilterSectionProps) => {
    const node = useShouldScrollIntoView(!!shouldScrollIntoView);

    const { radioValues } = getRadioValues(filters);

    const handleOptionChange = (value: string) => {
        onOptionChange(value);
    };

    const radioValueElements = radioValues.map((radioValue, index) => (
        <div
            key={radioValue.value}
            style={index > 0 ? { marginTop: '16px' } : {}}
            data-testid="filter-section-item-select"
        >
            {children(radioValue)}
        </div>
    ));

    return (
        <div
            className="filterSection"
            data-testid={`filter-section__${title?.toString()?.toLowerCase()}`}
            ref={node}
        >
            <ReadMore
                initialIsExpanded={shouldScrollIntoView}
                ariaLabel={`View more ${title}`}
                previewChildInitializer={() =>
                    childClampState(
                        PREVIEW_CHILD_COUNT,
                        radioValueElements.length,
                    )
                }
            >
                <ChoiceListBase
                    name={`${title}_${isMobile ? 'mobile' : 'desktop'}`}
                    size={'small'}
                    selected={activeFilterOption || defaultActiveFilterOption}
                    onChange={(val: string) => handleOptionChange(val)}
                    title={title}
                    titleHidden={false}
                >
                    <ul className="filter-choice-items">
                        <ReadMoreContent
                            previewChildCount={PREVIEW_CHILD_COUNT}
                            previewContentType="children"
                        >
                            {radioValueElements}
                        </ReadMoreContent>
                    </ul>
                </ChoiceListBase>
                <ReadMoreToggle toggleClass="filter-toggle" />
            </ReadMore>
        </div>
    );
};

export default FilterSection;
